import { FC, Key, useEffect, useMemo, useState } from 'react';
import { useLocation, useMatch, useNavigate, useResolvedPath } from 'react-router-dom';
import { SelectableListDataType, SelectableList } from '../../../../shared/selectable-list/selectable-list';
import { useQuery } from '@apollo/client';
import {
  ApplicantCasesActiveCaseListContainerIdentityVerificationCasesV1ByApplicantQueryDocument,
  ApplicantCasesActiveCaseListContainerCredentialVerificationCasesV1ByApplicantQueryDocument,
  ApplicantCasesActiveCaseListContainerSendReportCasesV1ByApplicantQueryDocument
} from '../../../../../generated';
import { ComponentQueryLoader } from '../../../../shared/component-query-loader';
import { getItem } from '../../../../shared/local-storage';
import { formatCamelCase } from './case-list';

interface ActiveCaseListContainerProps {}

export const ActiveCaseListContainer: FC<ActiveCaseListContainerProps> = () => {
  const navigate = useNavigate();
  const applicantUserId = getItem('applicantUserId');
  const location = useLocation();
  const [selectedActiveCase, setSelectedActiveCase] = useState<SelectableListDataType | undefined>();
  // Identity Verification Cases
  const {
    data: identityVerificationCasesV1Data,
    loading: identityVerificationCasesV1Loading,
    error: identityVerificationCasesV1Error
  } = useQuery(ApplicantCasesActiveCaseListContainerIdentityVerificationCasesV1ByApplicantQueryDocument, {
    variables: {
      applicantId: applicantUserId
    }
  });
  // Credential Verification Cases
  const {
    data: credentialVerificationCasesV1Data,
    loading: credentialVerificationCasesV1Loading,
    error: credentialVerificationCasesV1Error
  } = useQuery(ApplicantCasesActiveCaseListContainerCredentialVerificationCasesV1ByApplicantQueryDocument, {
    variables: {
      applicantId: applicantUserId
    }
  });
  // Send Report Cases
  const {
    data: sendReportCasesV1Data,
    loading: sendReportCasesV1Loading,
    error: sendReportCasesV1Error
  } = useQuery(ApplicantCasesActiveCaseListContainerSendReportCasesV1ByApplicantQueryDocument, {
    variables: {
      applicantId: applicantUserId
    }
  });

  const formattedData = useMemo(() => {
    return [
      ...(identityVerificationCasesV1Data?.identityVerificationCasesV1ByApplicant?.map((item) => ({
        key: item?.id,
        initials: item?.caseName?.substring(0, 2)?.toUpperCase() ?? '',
        title: item?.caseName ?? '',
        timestamp: item?.lastActivityAt,
        progress: formatCamelCase(item?.state ?? ''),
        schemaVersion: item?.schemaVersion ?? '',
        caseType: item?.caseType ?? '',
        state: formatCamelCase(item?.state ?? '')
      })) || []),
      ...(credentialVerificationCasesV1Data?.credentialVerificationCasesV1ByApplicant?.map((item) => ({
        key: item?.id,
        initials: item?.caseName?.substring(0, 2)?.toUpperCase() ?? '',
        state: formatCamelCase(item?.state ?? ''),
        title: item?.caseName ?? '',
        timestamp: item?.lastActivityAt,
        progress: formatCamelCase(item?.state ?? ''),
        schemaVersion: item?.schemaVersion ?? '',
        caseType: item?.caseType ?? ''
      })) || []),
      ...(sendReportCasesV1Data?.sendReportCasesV1ByApplicant?.map((item) => ({
        key: item?.id,
        initials: item?.caseName?.substring(0, 2)?.toUpperCase() ?? '',
        state: formatCamelCase(item?.state ?? ''),
        title: item?.caseName ?? '',
        timestamp: item?.lastActivityAt,
        progress: formatCamelCase(item?.state ?? ''),
        schemaVersion: item?.schemaVersion ?? '',
        caseType: item?.caseType ?? ''
      })) || [])
    ].sort((a, b) => (a.timestamp > b.timestamp ? -1 : 1));
  }, [identityVerificationCasesV1Data, credentialVerificationCasesV1Data, sendReportCasesV1Data]);

  const activeCaseListRoutePath = useResolvedPath('');
  const selectedActiveCaseRouteMatch = useMatch(`${activeCaseListRoutePath.pathname}/:caseType/:caseId/active/*`);
  const selectedActiveDraftCaseRouteMatch = useMatch(`${activeCaseListRoutePath.pathname}/:caseType/:caseId/draft`);

  useEffect(() => {
    setSelectedActiveCase(undefined);
    if (selectedActiveCaseRouteMatch) {
      setSelectedActiveCase(formattedData.find((r) => r.key?.toString() === selectedActiveCaseRouteMatch.params['caseId']));
      navigate(selectedActiveCaseRouteMatch.pathname);
    } else if (selectedActiveDraftCaseRouteMatch) {
      setSelectedActiveCase(formattedData.find((r) => r.key?.toString() === selectedActiveDraftCaseRouteMatch.params['caseId']));
      navigate(selectedActiveDraftCaseRouteMatch.pathname);
    }
  }, [formattedData, selectedActiveCaseRouteMatch, location.pathname]);

  const onCaseSelected = (selectedRowKey: Key, caseType: string, schemaVersion: string) => {
    const selectedCase = formattedData.find((r) => r.key === selectedRowKey);
    if (selectedCase?.progress === 'Draft') {
      setSelectedActiveCase(selectedCase);
      navigate(`${caseType}/${selectedRowKey}/draft`);
      return;
    }

    setSelectedActiveCase(selectedCase);
    navigate(`${caseType}/${selectedRowKey}/active/v${schemaVersion[0]}`);
  };

  return (
    <ComponentQueryLoader
      error={identityVerificationCasesV1Error || credentialVerificationCasesV1Error || sendReportCasesV1Error}
      loading={identityVerificationCasesV1Loading || credentialVerificationCasesV1Loading || sendReportCasesV1Loading}
      hasData={formattedData !== undefined}
      hasDataComponent={<SelectableList data={formattedData} selectedRecord={selectedActiveCase} onSelectChange={onCaseSelected} />}
    />
  );
};
